import type {
  LoginOptionsResponse,
  SessionBootstrapResponse,
  SubmissionSummary
} from "@camp-points/shared";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

export async function getLoginOptions(
  slug: string
): Promise<LoginOptionsResponse> {
  const callable = httpsCallable<
    { slug: string },
    LoginOptionsResponse
  >(functions, "getLoginOptions");

  return (await callable({ slug })).data;
}

export async function loginWithPin(input: {
  slug: string;
  membershipId: string;
  pin: string;
}): Promise<{
  customToken: string;
  organizationId: string;
  mustChangePin: boolean;
  displayName: string;
}> {
  const callable = httpsCallable<
    typeof input,
    {
      customToken: string;
      organizationId: string;
      mustChangePin: boolean;
      displayName: string;
    }
  >(functions, "loginWithPin");

  return (await callable(input)).data;
}

export async function getSessionBootstrap(
  organizationId?: string
): Promise<SessionBootstrapResponse> {
  const callable = httpsCallable<
    { organizationId?: string },
    SessionBootstrapResponse
  >(functions, "getSessionBootstrap");

  return (
    await callable(
      organizationId ? { organizationId } : {}
    )
  ).data;
}

export async function changePin(input: {
  organizationId: string;
  currentPin?: string;
  newPin: string;
}): Promise<{ success: boolean }> {
  const callable = httpsCallable<
    typeof input,
    { success: boolean }
  >(functions, "changePin");

  return (await callable(input)).data;
}

export interface CreateSubmissionInput {
  organizationId: string;
  categoryId: string;
  payload: Record<string, unknown>;
  notes: string;
  clientRequestId: string;
  allowDuplicate?: boolean;
  submissionIntent?: "standard" | "repeat";
  sourceSubmissionId?: string;
}

export async function createSubmission(
  input: CreateSubmissionInput
): Promise<{
  id: string;
  status: string;
  submissionNumber: string;
  totals: { rowCount: number; netPoints: number };
}> {
  const callable = httpsCallable<
    CreateSubmissionInput,
    {
      id: string;
      status: string;
      submissionNumber: string;
      totals: { rowCount: number; netPoints: number };
    }
  >(functions, "createSubmission");

  return (await callable(input)).data;
}

export async function listRecentSubmissions(
  organizationId: string,
  limit = 30
): Promise<SubmissionSummary[]> {
  const callable = httpsCallable<
    { organizationId: string; limit: number },
    { items: SubmissionSummary[] }
  >(functions, "listRecentSubmissions");

  return (
    await callable({ organizationId, limit })
  ).data.items;
}

export async function reverseSubmission(input: {
  organizationId: string;
  submissionId: string;
  reason?: string;
}): Promise<{ success: boolean }> {
  const callable = httpsCallable<
    typeof input,
    { success: boolean }
  >(functions, "reverseSubmission");

  return (await callable(input)).data;
}

export async function editSubmission(input: {
  organizationId: string;
  submissionId: string;
  payload: Record<string, unknown>;
  notes: string;
  clientRequestId: string;
}): Promise<{
  id: string;
  status: string;
  submissionNumber: string;
  totals: { rowCount: number; netPoints: number };
}> {
  const callable = httpsCallable<
    typeof input,
    {
      id: string;
      status: string;
      submissionNumber: string;
      totals: { rowCount: number; netPoints: number };
    }
  >(functions, "editSubmission");

  return (await callable(input)).data;
}

export async function getAdminPortalData(input: {
  organizationId: string;
  submissionLimit?: number;
}): Promise<import("@camp-points/shared").AdminPortalData> {
  const callable = httpsCallable<typeof input, import("@camp-points/shared").AdminPortalData>(functions, "getAdminPortalData");
  return (await callable(input)).data;
}

export async function createStaffMember(input: {
  organizationId: string;
  displayName: string;
  loginName?: string;
  email?: string;
  temporaryPin: string;
  role: "staff" | "seniorStaff" | "admin" | "superAdmin";
  permissions?: Partial<import("@camp-points/shared").PermissionMap>;
}): Promise<{ success: boolean; userId: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; userId: string }>(functions, "createStaffMember");
  return (await callable(input)).data;
}

export async function updateStaffMember(input: {
  organizationId: string;
  userId: string;
  displayName: string;
  loginName: string;
  email: string;
  role: "staff" | "seniorStaff" | "admin" | "superAdmin";
  permissions: import("@camp-points/shared").PermissionMap;
  active: boolean;
  loginVisible: boolean;
  sortOrder: number;
}): Promise<{ success: boolean }> {
  const callable = httpsCallable<typeof input, { success: boolean }>(functions, "updateStaffMember");
  return (await callable(input)).data;
}

export async function resetStaffPin(input: {
  organizationId: string;
  userId: string;
  temporaryPin: string;
}): Promise<{ success: boolean }> {
  const callable = httpsCallable<typeof input, { success: boolean }>(functions, "resetStaffPin");
  return (await callable(input)).data;
}

export async function saveCabin(input: {
  organizationId: string;
  cabinId?: string;
  name: string;
  shortName: string;
  number: number | null;
  color: string;
  textColor: string;
  sortOrder: number;
  activeGlobally: boolean;
  archived: boolean;
}): Promise<{ success: boolean; cabinId: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; cabinId: string }>(functions, "saveCabin");
  return (await callable(input)).data;
}

export async function saveWeek(input: {
  organizationId: string;
  weekId?: string;
  name: string;
  startDate: string;
  endDate: string;
  status: "upcoming" | "active" | "closed" | "archived";
  activeCabinIds: string[];
  enabledCategoryIds: string[];
  makeCurrent: boolean;
}): Promise<{ success: boolean; weekId: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; weekId: string }>(functions, "saveWeek");
  return (await callable(input)).data;
}

export async function saveCategory(input: {
  organizationId: string;
  categoryId?: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  enabled: boolean;
  archived: boolean;
  sortOrder: number;
  scoringMode: import("@camp-points/shared").CategoryScoringMode;
  formDefinition: Record<string, unknown>;
  scoringDefinition: Record<string, unknown>;
  approvalPolicy: { mode: "none" | "permissionBased" | "always"; permissionKey?: string };
  achievementDefinition: { enabled: boolean; name: string; descriptionTemplate: string };
  reportingDefinition?: {
    trackOccurrences?: boolean;
    trackWins?: boolean;
    winComponents?: string[];
    countLabel?: string;
    listLabel?: string;
    eventLabelMode?: "day" | "optionalName" | "nameRequired";
  };
}): Promise<{ success: boolean; categoryId: string; version: number }> {
  const callable = httpsCallable<typeof input, { success: boolean; categoryId: string; version: number }>(functions, "saveCategory");
  return (await callable(input)).data;
}

export async function updateOrganizationSettings(input: {
  organizationId: string;
  settings: Omit<import("@camp-points/shared").GeneralSettings, "updatedAt" | "updatedBy">;
  branding: import("@camp-points/shared").OrganizationBranding;
}): Promise<{ success: boolean }> {
  const callable = httpsCallable<typeof input, { success: boolean }>(functions, "updateOrganizationSettings");
  return (await callable(input)).data;
}

export async function reviewSubmission(input: {
  organizationId: string;
  submissionId: string;
  decision: "approve" | "decline";
  reason?: string;
}): Promise<{ success: boolean; status: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; status: string }>(functions, "reviewSubmission");
  return (await callable(input)).data;
}

export interface BulkPointChangeInput {
  organizationId: string;
  filter: { weekId?: string; categoryId?: string; cabinId?: string; currentPoints?: number };
  transform: { mode: "replace" | "add" | "multiply"; value: number };
}

export interface BulkPointPreview {
  items: Array<{
    entryId: string;
    submissionId: string;
    cabinId: string;
    categoryId: string;
    categoryName: string;
    beforePoints: number;
    afterPoints: number;
  }>;
  matched: number;
  actionable: number;
  pointsBefore: number;
  pointsAfter: number;
  truncated: boolean;
}

export async function previewBulkPointChange(input: BulkPointChangeInput): Promise<BulkPointPreview> {
  const callable = httpsCallable<BulkPointChangeInput, BulkPointPreview>(functions, "previewBulkPointChange");
  return (await callable(input)).data;
}

export async function applyBulkPointChange(input: BulkPointChangeInput & { reason: string }): Promise<BulkPointPreview & { success: boolean; operationId: string }> {
  const callable = httpsCallable<typeof input, BulkPointPreview & { success: boolean; operationId: string }>(functions, "applyBulkPointChange");
  return (await callable(input)).data;
}

export async function rollbackBulkOperation(input: {
  organizationId: string;
  operationId: string;
  reason: string;
}): Promise<{ success: boolean }> {
  const callable = httpsCallable<typeof input, { success: boolean }>(functions, "rollbackBulkOperation");
  return (await callable(input)).data;
}

export async function adminCorrectSubmission(input: {
  organizationId: string;
  submissionId: string;
  notes: string;
  reason: string;
  entries: Array<{ cabinId: string; points: number; note: string }>;
}): Promise<{ success: boolean; submissionId: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; submissionId: string }>(functions, "adminCorrectSubmission");
  return (await callable(input)).data;
}


export async function getReportCenterData(input: {
  organizationId: string;
  weekId: string;
}): Promise<import("@camp-points/shared").ReportCenterData> {
  const callable = httpsCallable<typeof input, import("@camp-points/shared").ReportCenterData>(functions, "getReportCenterData");
  return (await callable(input)).data;
}

export async function saveReportTemplate(input: {
  organizationId: string;
  templateId?: string;
  name: string;
  description: string;
  enabled: boolean;
  isDefault: boolean;
  layout: "standard" | "booklet";
  sections: import("@camp-points/shared").ReportSectionConfig[];
  style: import("@camp-points/shared").ReportTemplateDocument["style"];
}): Promise<{ success: boolean; templateId: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; templateId: string }>(functions, "saveReportTemplate");
  return (await callable(input)).data;
}

export async function saveAchievementRule(input: {
  organizationId: string;
  ruleId?: string;
  name: string;
  icon: string;
  descriptionTemplate: string;
  enabled: boolean;
  metric: import("@camp-points/shared").AchievementMetric;
  categoryId: string | null;
  comparator: import("@camp-points/shared").AchievementRuleDocument["comparator"];
  threshold: number;
  showOnReport: boolean;
  sortOrder: number;
}): Promise<{ success: boolean; ruleId: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; ruleId: string }>(functions, "saveAchievementRule");
  return (await callable(input)).data;
}

export async function saveSubmissionReportMetadata(input: {
  organizationId: string;
  submissionId: string;
  reportEventLabel: string;
  reportDayLabel: string;
}): Promise<{ success: boolean }> {
  const callable = httpsCallable<typeof input, { success: boolean }>(functions, "saveSubmissionReportMetadata");
  return (await callable(input)).data;
}

export async function saveReportComment(input: {
  organizationId: string;
  weekId: string;
  cabinId: string;
  comment: string;
}): Promise<{ success: boolean; commentId: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; commentId: string }>(functions, "saveReportComment");
  return (await callable(input)).data;
}

export async function createReportSnapshot(input: {
  organizationId: string;
  weekId: string;
  cabinId: string;
  templateId: string;
  reportData: Record<string, unknown>;
}): Promise<{ success: boolean; snapshotId: string }> {
  const callable = httpsCallable<typeof input, { success: boolean; snapshotId: string }>(functions, "createReportSnapshot");
  return (await callable(input)).data;
}
